import React from 'react';
import { WeeklyTimesheet, WeeklyTimesheetEntry } from '../types/timesheet.ts';
import { X, Calendar, FileText, Users } from 'lucide-react';

interface TimesheetDetailViewProps {
  timesheet: WeeklyTimesheet;
  onClose: () => void;
}

const TimesheetDetailView: React.FC<TimesheetDetailViewProps> = ({ timesheet, onClose }) => {
  // Debug: Log the timesheet data to see what we're working with
  React.useEffect(() => {
    console.log('Timesheet data:', timesheet);
    console.log('First entry regularHours:', timesheet.entries[0]?.regularHours);
    console.log('First entry overtimeHours:', timesheet.entries[0]?.overtimeHours);
  }, [timesheet]);
  const formatHours = (regular: number, overtime: number) => {
    if (regular === 0 && overtime === 0) return '-';
    
    return (
      <div className="text-center">
        <div className="text-sm font-medium text-gray-900">{regular || 0}</div>
        {overtime > 0 && (
          <div className="text-xs text-orange-600 font-semibold">+{overtime} OT</div>
        )}
      </div>
    );
  };

  const getDayHours = (entry: WeeklyTimesheetEntry, day: keyof WeeklyTimesheetEntry['hours']) => {
    const regularHours = entry.regularHours?.[day] || 0;
    const overtimeHours = entry.overtimeHours?.[day] || 0;
    const totalHours = entry.hours[day] || 0;
    
    // If we have Regular/OT breakdown data, use it
    if (entry.regularHours && entry.overtimeHours) {
      return { regular: regularHours, overtime: overtimeHours };
    }
    
    // Legacy fallback: For old timesheets without Regular/OT breakdown
    // Assume all hours are regular for now (could be enhanced with business rules)
    return { regular: totalHours, overtime: 0 };
  };

  const getTotalHours = (entry: WeeklyTimesheetEntry) => {
    const days: (keyof WeeklyTimesheetEntry['hours'])[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    let totalRegular = 0;
    let totalOvertime = 0;
    let grandTotal = 0;

    days.forEach(day => {
      const { regular, overtime } = getDayHours(entry, day);
      totalRegular += regular;
      totalOvertime += overtime;
      grandTotal += entry.hours[day] || 0;
    });

    return { totalRegular, totalOvertime, grandTotal };
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <FileText className="h-7 w-7 mr-3 text-blue-600" />
              {timesheet.jobName}
            </h2>
            <div className="flex items-center space-x-6 mt-2 text-sm text-gray-600">
              {timesheet.jobNumber && (
                <span className="flex items-center">
                  <span className="font-medium">Job #{timesheet.jobNumber}</span>
                </span>
              )}
              <span className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                Week ending: {timesheet.weekEnding.toLocaleDateString()}
              </span>
              <span className="flex items-center">
                <Users className="h-4 w-4 mr-1" />
                {timesheet.entries.length} employee{timesheet.entries.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Legacy Notice */}
        {(!timesheet.entries[0]?.regularHours || !timesheet.entries[0]?.overtimeHours) && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-amber-800">
                  <strong>Legacy Timesheet:</strong> This timesheet was created before Regular/OT tracking was implemented. 
                  All hours are shown as Regular. Future imports will automatically separate Regular vs Overtime hours.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Timesheet Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mon<br/><small>R/OT</small>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tue<br/><small>R/OT</small>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Wed<br/><small>R/OT</small>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Thu<br/><small>R/OT</small>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fri<br/><small>R/OT</small>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sat<br/><small>R/OT</small>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sun<br/><small>R/OT</small>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total<br/><small>R/OT</small>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {timesheet.entries.map((entry, index) => {
                const { totalRegular, totalOvertime, grandTotal } = getTotalHours(entry);
                
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {entry.employeeName}
                      </div>
                      {entry.activityCode && (
                        <div className="text-xs text-gray-500">
                          {entry.activityCode}: {entry.activityDescription}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {formatHours(...Object.values(getDayHours(entry, 'monday')))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {formatHours(...Object.values(getDayHours(entry, 'tuesday')))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {formatHours(...Object.values(getDayHours(entry, 'wednesday')))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {formatHours(...Object.values(getDayHours(entry, 'thursday')))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {formatHours(...Object.values(getDayHours(entry, 'friday')))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {formatHours(...Object.values(getDayHours(entry, 'saturday')))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {formatHours(...Object.values(getDayHours(entry, 'sunday')))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm font-bold text-gray-900">{totalRegular.toFixed(1)}</div>
                      {totalOvertime > 0 && (
                        <div className="text-xs text-orange-600 font-semibold">+{totalOvertime.toFixed(1)} OT</div>
                      )}
                      <div className="text-xs text-gray-500 border-t pt-1 mt-1">
                        Total: {grandTotal.toFixed(1)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Summary */}
        <div className="mt-6 bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Timesheet Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {timesheet.entries.reduce((sum, entry) => {
                  const { totalRegular } = getTotalHours(entry);
                  return sum + totalRegular;
                }, 0).toFixed(1)}
              </div>
              <div className="text-sm text-gray-600">Regular Hours</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {timesheet.entries.reduce((sum, entry) => {
                  const { totalOvertime } = getTotalHours(entry);
                  return sum + totalOvertime;
                }, 0).toFixed(1)}
              </div>
              <div className="text-sm text-gray-600">Overtime Hours</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {timesheet.entries.reduce((sum, entry) => {
                  const { grandTotal } = getTotalHours(entry);
                  return sum + grandTotal;
                }, 0).toFixed(1)}
              </div>
              <div className="text-sm text-gray-600">Total Hours</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{timesheet.entries.length}</div>
              <div className="text-sm text-gray-600">Employee{timesheet.entries.length !== 1 ? 's' : ''}</div>
            </div>
          </div>
        </div>

        {/* Close Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimesheetDetailView;